import { Download, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Calendar } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
const PRIMARY_COLOR = '#003366';
const CustomDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [isSelectingStart, setIsSelectingStart] = useState(true);


  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setTempStartDate(null);
        setTempEndDate(null);
        setIsSelectingStart(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const handleDateSelect = (date) => {
    if (isSelectingStart || !tempStartDate) {
      setTempStartDate(date);
      setTempEndDate(null);
      setIsSelectingStart(false);
    } else {
      if (date < tempStartDate) {
        setTempStartDate(date);
        setTempEndDate(null);
      } else {
        setTempEndDate(date);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date &&
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    return date >= tempStartDate && date <= tempEndDate;
  };

  const isStartDate = (date) => {
    return tempStartDate && date &&
      date.getDate() === tempStartDate.getDate() &&
      date.getMonth() === tempStartDate.getMonth() &&
      date.getFullYear() === tempStartDate.getFullYear();
  };

  const isEndDate = (date) => {
    return tempEndDate && date &&
      date.getDate() === tempEndDate.getDate() &&
      date.getMonth() === tempEndDate.getMonth() &&
      date.getFullYear() === tempEndDate.getFullYear();
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      onChange({
        startDate: tempStartDate,
        endDate: tempEndDate
      });
      setIsOpen(false);
      setTempStartDate(null);
      setTempEndDate(null);
      setIsSelectingStart(true);
    }
  };

  const clearSelection = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setIsSelectingStart(true);
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} — ${formatDisplayDate(endDate)}`;
    }
    return placeholder;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="relative" ref={datePickerRef}>
      <div
        className="w-full p-1.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-opacity-20 transition-colors bg-white cursor-pointer flex items-center justify-between"
        style={{
          '--tw-ring-color': PRIMARY_COLOR,
          borderColor: isOpen ? PRIMARY_COLOR : undefined
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={startDate && endDate ? "text-gray-900 text-sm" : "text-gray-400 text-sm"}>
          {getDisplayText()}
        </span>
        <div className="flex items-center space-x-1">
          <Calendar className="w-4 h-4 text-gray-600" />
          {isOpen ?
            <ChevronUp className="w-4 h-4 text-gray-600" /> :
            <ChevronDown className="w-4 h-4 text-gray-600" />
          }
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-md z-50 w-72">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-2 border-b border-gray-100">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-50 rounded-full transition-colors"
            >
              <ChevronLeft className="w-3 h-3 text-gray-600" />
            </button>

            <span className="font-medium text-gray-900 text-sm">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>

            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-50 rounded-full transition-colors"
            >
              <ChevronRight className="w-3 h-3 text-gray-600" />
            </button>
          </div>

          {/* Selection instruction */}
          <div className="px-2 py-1 bg-gray-50 text-xs text-gray-700">
            {isSelectingStart || !tempStartDate ?
              'Select start date' :
              (tempEndDate ?
                `${formatDisplayDate(tempStartDate)} — ${formatDisplayDate(tempEndDate)}` :
                `Start: ${formatDisplayDate(tempStartDate)} • Select end date`
              )
            }
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 p-1 border-b border-gray-100">
            {daysOfWeek.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1 p-1">
            {days.map((date, index) => {
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              const inRange = isInRange(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && handleDateSelect(date)}
                  disabled={!date}
                  className={`
                    h-7 w-7 text-xs rounded-md transition-colors flex items-center justify-center
                    ${!date ? 'invisible' : ''}
                    ${isStart || isEnd
                      ? 'text-white font-medium'
                      : inRange
                        ? 'text-gray-900'
                        : today
                          ? 'bg-gray-100 text-gray-900 font-medium border border-gray-300'
                          : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                  style={{
                    backgroundColor: isStart || isEnd ? PRIMARY_COLOR : inRange ? '#e6f0ff' : undefined
                  }}
                >
                  {date?.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-2 border-t border-gray-100">
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear
            </button>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyDateRange}
                disabled={!tempStartDate || !tempEndDate}
                className="text-xs text-white px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: !tempStartDate || !tempEndDate ? '#9ca3af' : PRIMARY_COLOR,
                  borderColor: PRIMARY_COLOR
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = '#004080';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = PRIMARY_COLOR;
                  }
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;